import { getAuth, type Auth, type AuthData } from "home-assistant-js-websocket";

const STORAGE_PREFIX = "rsc-dash-ha-auth:";
const LOCAL_CHANNEL_HEADER = "X-RSC-Local-Channel";

function storageKey(hassUrl: string): string {
  return `${STORAGE_PREFIX}${hassUrl}`;
}

function readStoredTokens(hassUrl: string): AuthData | null {
  try {
    const raw = window.localStorage.getItem(storageKey(hassUrl));
    return raw ? (JSON.parse(raw) as AuthData) : null;
  } catch {
    // Corrupt entry or localStorage unavailable (private browsing, quota) —
    // treat as "no session", getAuth() will redirect to login.
    return null;
  }
}

function writeStoredTokens(hassUrl: string, data: AuthData | null): void {
  try {
    if (data) {
      window.localStorage.setItem(storageKey(hassUrl), JSON.stringify(data));
    } else {
      window.localStorage.removeItem(storageKey(hassUrl));
    }
  } catch {
    // Nothing we can do — auth just won't persist across reloads.
  }
}

export async function isLocalChannel(
  fetcher: typeof fetch = window.fetch.bind(window),
): Promise<boolean> {
  try {
    const response = await fetcher(`${window.location.origin}/`, {
      method: "HEAD",
      credentials: "same-origin",
      cache: "no-store",
    });
    return response.ok && response.headers.get(LOCAL_CHANNEL_HEADER) === "1";
  } catch {
    return false;
  }
}

/**
 * Builds the same callback state consumed by home-assistant-js-websocket,
 * adding only the two RSControl markers that select HA's local provider.
 */
export function buildLocalAuthorizeUrl(hassUrl: string): string {
  const normalizedHassUrl = hassUrl.replace(/\/$/, "");
  const clientId = `${window.location.origin}/`;
  const redirect = new URL(window.location.href);
  redirect.hash = "";
  redirect.searchParams.set("auth_callback", "1");
  const state = window.btoa(
    JSON.stringify({ hassUrl: normalizedHassUrl, clientId }),
  );
  const authorize = new URL("/auth/authorize", normalizedHassUrl);
  authorize.searchParams.set("response_type", "code");
  authorize.searchParams.set("redirect_uri", redirect.toString());
  authorize.searchParams.set("client_id", clientId);
  authorize.searchParams.set("state", state);
  authorize.searchParams.set("skip_oidc_redirect", "true");
  authorize.searchParams.set("rsc_auth_provider", "homeassistant");
  return authorize.toString();
}

export interface NativeAuthBridge {
  version: 1;
  login(options: { hassUrl: string }): Promise<AuthData>;
  cancel(): Promise<void>;
}
declare global { interface Window { rscElastronAuth?: NativeAuthBridge } }

function bridgeFor(hassUrl: string): NativeAuthBridge | undefined {
  const bridge = window.rscElastronAuth;
  if (bridge === undefined) return;
  const url = new URL(hassUrl);
  if (bridge.version !== 1 || typeof bridge.login !== "function" || typeof bridge.cancel !== "function" ||
      url.protocol !== "https:" || url.origin !== window.location.origin || url.username || url.password) {
    throw new Error("AUTH_BRIDGE_INVALID");
  }
  return bridge;
}
function validateTokens(data: AuthData, hassUrl: string): AuthData {
  try {
    const url = new URL(data.hassUrl);
    const client = new URL(data.clientId ?? "");
    if (url.href !== new URL(hassUrl).href || url.username || url.password ||
        client.protocol !== "http:" || client.hostname !== "127.0.0.1" || !client.port ||
        client.pathname !== "/" || client.search || client.hash || client.username || client.password ||
        typeof data.access_token !== "string" || !data.access_token ||
        typeof data.refresh_token !== "string" || !data.refresh_token ||
        !Number.isFinite(data.expires_in) || data.expires_in <= 0 ||
        !Number.isFinite(data.expires) || data.expires <= Date.now()) throw new Error();
    return { ...data, hassUrl };
  } catch { throw new Error("AUTH_TOKEN_INVALID"); }
}

type LocalAuthBoundary = {
  fetcher?: typeof fetch;
  navigate?: (url: string) => void;
  signal?: AbortSignal;
  timeoutMs?: number;
};

// One writer per canonical HA URL. Old refresh/callback continuations never
// regain write authority, including after logout or a StrictMode remount.
type Session = {
  url: string;
  active: boolean;
  bridge?: NativeAuthBridge;
  auth?: Auth;
  signal: AbortSignal;
  invalidate: () => void;
  cancel: () => Promise<void>;
};
const owners = new Map<string, Session>();
const sessions = new WeakMap<Auth, Session>();
const logouts = new WeakMap<Auth, Promise<void>>();

function canonicalUrl(value: string): string {
  const url = new URL(value);
  if (!/^https?:$/.test(url.protocol) || url.username || url.password || url.search || url.hash) {
    throw new Error("AUTH_ORIGIN_INVALID");
  }
  // Library 9.6.0 removes exactly the trailing slash before comparing tokens.
  return url.href.replace(/\/+$/, "");
}
function owns(session: Session): boolean { return owners.get(session.url) === session; }
function requireActive(session: Session): void {
  if (!session.active || !owns(session)) throw new Error("AUTH_CANCELLED");
}
async function bounded(action: () => Promise<unknown>, ms = 10_000): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([action(), new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error("AUTH_TIMEOUT")), ms);
    })]);
  } catch { /* best effort, never expose network error text */ }
  finally { clearTimeout(timer); }
}

/** Bounds auth/navigation and grants token writes only to the current lifecycle. */
export async function resolveHAAuth(hassUrl: string, boundary: LocalAuthBoundary = {}): Promise<Auth> {
  const url = canonicalUrl(hassUrl);
  const previous = owners.get(url);
  previous?.invalidate();
  // Invoke cancellation before assigning the new generation. A late old cleanup
  // must never call the singleton bridge's cancel against a newer transaction.
  if (previous && owns(previous)) void previous.cancel();
  const controller = new AbortController();
  let rejectStopped: (error: Error) => void = () => undefined;
  let cancelPromise: Promise<void> | undefined;
  let failure = "AUTH_CANCELLED";
  const session: Session = {
    url, active: true, signal: controller.signal,
    cancel: () => {
      if (!cancelPromise) cancelPromise = owns(session)
        ? bounded(async () => { await session.bridge?.cancel(); }) : Promise.resolve();
      return cancelPromise;
    },
    invalidate: () => {
      if (!session.active) return;
      session.active = false;
      controller.abort();
      clearTimeout(timer);
      boundary.signal?.removeEventListener("abort", abort);
      rejectStopped(new Error("AUTH_CANCELLED"));
    },
  };
  const abort = () => { session.invalidate(); void session.cancel(); };
  owners.set(url, session);
  const stopped = new Promise<never>((_, reject) => { rejectStopped = reject; });
  boundary.signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(() => { failure = "AUTH_TIMEOUT"; session.invalidate(); void session.cancel(); }, boundary.timeoutMs ?? 300_000);
  if (boundary.signal?.aborted) abort();
  const fetcher: typeof fetch = (input, init) => (boundary.fetcher ?? window.fetch.bind(window))(input, { ...init, signal: controller.signal });
  const resolve = async () => {
    requireActive(session);
    session.bridge = bridgeFor(url);
    let nativeTokens: AuthData | undefined;
    if (session.bridge && !readStoredTokens(url)) {
      const data = validateTokens(await session.bridge.login({ hassUrl: url }), url);
      requireActive(session);
      nativeTokens = data;
    }
    requireActive(session);
    if (session.bridge) stripAuthCallbackParams();
    const stored = nativeTokens ?? readStoredTokens(url);
    const callback = new URLSearchParams(window.location.search).has("auth_callback");
    if (!session.bridge && !stored && !callback) {
      const local = await isLocalChannel(fetcher);
      requireActive(session);
      const authorize = new URL(buildLocalAuthorizeUrl(url));
      if (!local) {
        authorize.searchParams.delete("skip_oidc_redirect");
        authorize.searchParams.delete("rsc_auth_provider");
      }
      (boundary.navigate ?? ((target) => window.location.assign(target)))(authorize.toString());
      return new Promise<Auth>(() => undefined);
    }
    requireActive(session);
    const auth = await getAuth({
      hassUrl: url,
      limitHassInstance: true,
      loadTokens: async () => {
        requireActive(session);
        const data = nativeTokens ?? readStoredTokens(url);
        // Never let the library navigate after an asynchronous loadTokens.
        if (!data) throw new Error("AUTH_SESSION_MISSING");
        if (canonicalUrl(data.hassUrl) !== url) throw new Error("AUTH_TOKEN_INVALID");
        requireActive(session);
        return { ...data, hassUrl: url };
      },
      saveTokens: (data) => {
        if (session.active && owns(session)) writeStoredTokens(url, data);
      },
    });
    requireActive(session);
    if (auth.refreshAccessToken) {
      const refresh = auth.refreshAccessToken.bind(auth);
      auth.refreshAccessToken = async () => {
        requireActive(session);
        await refresh();
        requireActive(session);
      };
    }
    if (nativeTokens) writeStoredTokens(url, auth.data);
    session.auth = auth;
    sessions.set(auth, session);
    return auth;
  };
  try { return await Promise.race([resolve(), stopped]); }
  catch {
    const code = session.active ? "AUTH_FAILED" : failure;
    session.invalidate();
    void session.cancel();
    throw new Error(code);
  } finally { clearTimeout(timer); }
}

// Serialize actual browser Cookie/Set-Cookie mutations, not only caller results.
// A timed-out caller never releases this queue before its fetch really settles.
let cookieWrites: Promise<unknown> = Promise.resolve();
let cookieCleanupRequired = false;
function cookieWrite<T>(action: () => Promise<T>): Promise<T> {
  const result = cookieWrites.then(action, action);
  cookieWrites = result;
  void result.catch(() => undefined);
  return result;
}

async function cameraDeadline<T>(signals: AbortSignal[], action: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  let rejectStopped!: (reason: Error) => void;
  const stopped = new Promise<never>((_, reject) => { rejectStopped = reject; });
  const abort = () => { controller.abort(); rejectStopped(new Error("CAMERA_SESSION_UNAVAILABLE")); };
  signals.forEach(signal => signal.addEventListener("abort", abort, { once: true }));
  const timer = setTimeout(abort, 10_000);
  if (signals.some(signal => signal.aborted)) abort();
  try { return await Promise.race([stopped, action(controller.signal)]); }
  finally { clearTimeout(timer); signals.forEach(signal => signal.removeEventListener("abort", abort)); }
}

export interface SurveillanceSessionLease { deadline: number; signal: AbortSignal }

/** Mint the existing HA credential within one refresh+queue+HTTP+body deadline. */
export async function prepareSurveillanceSession(signal: AbortSignal, fetcher: typeof fetch = window.fetch.bind(window)): Promise<SurveillanceSessionLease> {
  if (window.location.protocol !== "https:") throw new Error("CAMERA_SESSION_UNAVAILABLE");
  const session = owners.get(canonicalUrl(window.location.origin));
  if (!session?.auth) throw new Error("CAMERA_SESSION_UNAVAILABLE");
  const auth = session.auth;
  return cameraDeadline([signal, session.signal], async operation => {
    const check = () => { requireActive(session); if (operation.aborted) throw new Error("CAMERA_SESSION_UNAVAILABLE"); };
    check();
    if (auth.expired) await auth.refreshAccessToken();
    check();
    const response = await cookieWrite(() => {
      check();
      if (cookieCleanupRequired) throw new Error("CAMERA_SESSION_UNAVAILABLE");
      return fetcher(new URL("/api/rscontrol/local_session", window.location.origin).href, {
        method: "POST", credentials: "same-origin", cache: "no-store", redirect: "error",
        headers: { Authorization: `Bearer ${auth.data.access_token}` }, signal: operation,
      });
    });
    check();
    if (response.status !== 200 || !response.headers.get("Content-Type")?.startsWith("application/json")) throw new Error("CAMERA_SESSION_UNAVAILABLE");
    const reader = response.body?.getReader();
    if (!reader) throw new Error("CAMERA_SESSION_UNAVAILABLE");
    let text = "";
    let bytes = 0;
    const decoder = new TextDecoder();
    try {
      while (!operation.aborted) {
        const chunk = await reader.read();
        check();
        if (chunk.done) break;
        bytes += chunk.value.byteLength;
        if (bytes > 1024) throw new Error("CAMERA_SESSION_UNAVAILABLE");
        text += decoder.decode(chunk.value, { stream: true });
      }
      text += decoder.decode();
      check();
      const body = JSON.parse(text) as { expires?: unknown };
      const remaining = typeof body.expires === "number" && Number.isInteger(body.expires)
        ? body.expires * 1000 - Date.now() : 0;
      // Existing Core contract: max15min. Remove iframe at least1s early.
      if (remaining <= 1000 || remaining > 900_000) throw new Error("CAMERA_SESSION_UNAVAILABLE");
      return { deadline: performance.now() + remaining - 1000, signal: session.signal };
    } catch { throw new Error("CAMERA_SESSION_UNAVAILABLE"); }
    finally { void reader.cancel().catch(() => undefined); }
  });
}

/** Reserve cleanup before teardown can synchronously create another owner. */
function removeCameraCookie(): Promise<void> {
  cookieCleanupRequired = true;
  return cameraDeadline([], async signal => {
    await cookieWrite(async () => {
      if (signal.aborted) throw new Error("AUTH_LOGOUT_INCOMPLETE");
      const response = await window.fetch(new URL("/api/rscontrol/local_session", window.location.origin).href, {
        method: "DELETE", credentials: "same-origin", cache: "no-store", redirect: "error", signal,
      });
      if (signal.aborted || response.status !== 204) throw new Error("AUTH_LOGOUT_INCOMPLETE");
      cookieCleanupRequired = false;
    });
  });
}

/** Clears storage and invalidates the current writer before any late refresh. */
export function clearStoredAuth(hassUrl: string): void {
  const url = canonicalUrl(hassUrl);
  owners.get(url)?.invalidate();
  writeStoredTokens(url, null);
}

/** Synchronous invalidation/connection teardown precede any awaited operation. */
export function logoutHA(auth: Auth, closeConnection: () => void = () => undefined): Promise<void> {
  const session = sessions.get(auth);
  // API-level generation guard: even a stale Auth sharing the same refresh
  // token must never revoke/cancel/close a newer generation.
  if (!session || !owns(session)) return Promise.resolve();
  const existing = logouts.get(auth);
  if (existing) return existing;
  const url = session.url;
  const owner = owners.get(url);
  const mayClear = () => owners.get(url) === (session ?? owner);
  session.invalidate();
  const cleanup = session.url === canonicalUrl(window.location.origin) && window.location.protocol === "https:"
    ? removeCameraCookie() : Promise.resolve();
  // Attach immediately; a hanging revoke must not leave cleanup rejection unhandled.
  void cleanup.catch(() => undefined);
  const cancelled = session.cancel();
  closeConnection();
  if (mayClear()) writeStoredTokens(url, null);
  const pending = (async () => {
    try {
      await Promise.all([cleanup, owns(session) ? bounded(() => auth.revoke()) : Promise.resolve(), cancelled]);
    } catch {
      logouts.delete(auth); // current owner may explicitly retry failed cookie cleanup
      throw new Error("AUTH_LOGOUT_INCOMPLETE");
    } finally { if (mayClear()) writeStoredTokens(url, null); }
  })();
  logouts.set(auth, pending);
  return pending;
}

/**
 * Strips the auth_callback/code/state query params HA appends on redirect
 * back from /auth/authorize, once getAuth() has consumed them — otherwise
 * they linger in the URL (and would be replayed as a stale code on reload).
 */
export function stripAuthCallbackParams(): void {
  if (!window.location.search.includes("auth_callback")) return;
  const url = new URL(window.location.href);
  url.searchParams.delete("auth_callback");
  url.searchParams.delete("code");
  url.searchParams.delete("state");
  window.history.replaceState({}, "", url.toString());
}
